import React from 'react';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Spinner from './spinner/Spinner';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import { fileToBase64 } from '../helpers/convertToBase64';

const schema = yup
  .object({
    courseName: yup.string().required('Course name is required'),
    courseDescription: yup.string().required('Course description is required'),
    courseImage: yup
      .mixed()
      .test('fileSize', 'Please upload image less than 2MB', (value) => {
        if (!value || value.length === 0) return true;
        return value[0].size <= 2000000;
      }),
  })
  .required();

function EditCourse({ toggleModal, course, courseId }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const queryClient = useQueryClient();

  const { mutate, isLoading: isSubmitting } = useMutation(
    (data) => axiosInstancev2.put(`/courses/${courseId}`, data),
    {
      onSuccess: () => {
        toast.success('Course updated successfully!');
        queryClient.invalidateQueries(['course', courseId]);
        queryClient.invalidateQueries(['courses']);
        toggleModal();
      },
      onError: () => {
        toast.error("Couldn't update course. Please try again.");
      },
    }
  );

  const onSubmit = async (data) => {
    let courseImage = '';
    if (data.courseImage?.[0]) {
      courseImage = await fileToBase64(data.courseImage[0]);
    }

    mutate({
      courseName: data.courseName,
      courseDescription: data.courseDescription,
      courseImage,
    });
  };

  return (
    <div>
      <form
        className="text-gray-700 space-y-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col space-y-1">
          <label htmlFor="editCourseName" className="text-base">
            Course Name
          </label>
          <input
            {...register('courseName', { required: true })}
            type="text"
            id="editCourseName"
            placeholder="Enter Course Name"
            defaultValue={course?.courseName}
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.courseName ? 'border-red-600' : ''
            }`}
          />
          {errors.courseName && (
            <span className="text-red-600 text-xs mt-2">
              {errors.courseName?.message}
            </span>
          )}
        </div>
        <div className="flex flex-col space-y-1">
          <label htmlFor="editCourseDescription" className="text-base">
            Course Description
          </label>
          <textarea
            {...register('courseDescription', { required: true })}
            rows={3}
            id="editCourseDescription"
            placeholder="Enter Course Description"
            defaultValue={course?.courseDescription}
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.courseDescription ? 'border-red-600' : ''
            }`}
          />
          {errors.courseDescription && (
            <span className="text-red-600 text-xs mt-2">
              {errors.courseDescription?.message}
            </span>
          )}
        </div>
        <div className="flex flex-col space-y-1">
          <label htmlFor="editCourseImage" className="text-base">
            Course Image
          </label>
          {course?.courseImage && (
            <div className="mb-2">
              <img
                src={course.courseImage}
                alt={course.courseName}
                className="h-32 object-contain rounded-md border border-gray-200"
              />
              <p className="text-xs text-gray-400 mt-1">Current image</p>
            </div>
          )}
          <input
            {...register('courseImage')}
            accept="image/gif, image/jpeg, image/png, image/webp"
            type="file"
            id="editCourseImage"
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.courseImage ? 'border-red-600' : ''
            }`}
          />
          <p className="text-xs text-gray-400">
            Leave empty to keep the current image
          </p>
          {errors.courseImage && (
            <span className="text-red-600 text-xs mt-2">
              {errors.courseImage?.message}
            </span>
          )}
        </div>
        {isSubmitting ? (
          <div className="grid items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex justify-between mt-8">
            <button
              className="border-[1px] border-claret-500 px-4 py-1 rounded-md text-claret-500 text-sm space-x-2 flex items-center"
              type="button"
              onClick={toggleModal}
            >
              <CancelIcon fontSize="inherit" />
              <p>Cancel</p>
            </button>
            <button
              className="bg-claret-500 px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center"
              type="submit"
            >
              <SaveIcon fontSize="inherit" />
              <p>Update Course</p>
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default EditCourse;
